import { ID_BADGE_ENABLED } from './featureFlags';

export const EARNING_MILESTONES = [
  { id: 'personal_details', label: 'Personal details' },
  { id: 'linkedin_credentials', label: 'LinkedIn credentials' },
  { id: 'id_badge', label: 'ID badge' },
  { id: 'email_verification', label: 'Email verification' },
  { id: 'final_submission', label: 'Final submission' },
];

// Landing redesign (2026-09-25, thread landing-pages-look-better): the App-level
// tracker is suppressed on the welcome step so the site identity can sit ABOVE it
// (Filip's order: identity, then earnings progress, then main section, then
// Trustpilot). Step01Welcome renders its own EarningsProgressTracker in that slot.
const WELCOME_STEP_NUMBER = 1;
const FINALIZING_STEP_NUMBER = 17;
const SESSION_CLEANUP_STEP_NUMBER = 18;
// seller-agent-messenger-step (2026-09-28): "Message your agent" sits between
// PaymentDetails and Confirmation, so Confirmation moved from 21 to 22. Payment is
// already submitted on the agent step, so final submission counts as earned there.
const MESSAGE_AGENT_STEP_NUMBER = 21;
const CONFIRMATION_STEP_NUMBER = 22;
const FINAL_DETAILS_STEP_NUMBER = 19;
const PAYMENT_DETAILS_STEP_NUMBER = 20;

function normaliseStep(step) {
  const parsed = Number.parseInt(step, 10);
  return Number.isFinite(parsed) ? parsed : 1;
}

function hasPassport(sessionData = {}) {
  return Array.isArray(sessionData.idTypes) && sessionData.idTypes.includes('Passport');
}

export function isIdBadgeExplicitlySkipped(sessionData = {}) {
  return sessionData.idBadgeUpsell === 'No';
}

// B-IDBADGE-HOLD-PENDING (2026-05-29): the seller PURSUED the badge upsell
// (said Yes, or tapped "I've added it"/"Continue" on Step12). Distinct from
// "already had a verified badge before the takeover" (login scrape) — only a
// claim makes the £5 bonus applicable, and the bonus is realized once the
// badge is actually verified (see isIdBadgeEarned).
export function isIdBadgeClaimed(sessionData = {}) {
  return sessionData.idBadgeUpsell === 'Yes'
    || sessionData.idBadgeAccepted === true
    || !!sessionData.idBadgeAddedConfirmedAt;
}

export function isIdBadgeNotApplicable(step, sessionData = {}) {
  const currentStep = normaliseStep(step);
  if (isIdBadgeExplicitlySkipped(sessionData)) return false;
  // A claimed badge stays applicable — it's earned once verified, not
  // "not applicable". Only "verified-but-never-claimed" below means the
  // seller already had the badge (no upsell pursued → no bonus).
  if (isIdBadgeClaimed(sessionData)) return false;
  if (sessionData.idBadgeAlreadyHad === true) return true;
  if (sessionData.verifiedBadgePresent === true || sessionData.hasVerifiedBadge === true) return true;
  if (Array.isArray(sessionData.idTypes) && !hasPassport(sessionData) && currentStep >= 8) {
    return true;
  }
  return false;
}

export function isIdBadgeEarned(step, sessionData = {}) {
  if (isIdBadgeExplicitlySkipped(sessionData)) return false;
  // B-IDBADGE-HOLD-PENDING (2026-05-29): the £5 badge bonus is only
  // "earned" once the badge is ACTUALLY verified on LinkedIn
  // (verified_badge_present === true) AND the seller pursued the upsell —
  // not on the mere claim (reaching step 13 / tapping "I've added it").
  // LinkedIn's verified badge is added via an async phone/Persona review
  // that does not surface in-session, so the old `currentStep >= 13 ||
  // idBadgeAddedConfirmedAt` shortcuts marked it earned before it was
  // real, promising sellers a £5 we couldn't confirm. The operator credits
  // the bonus out-of-band once verified. ("verified but not claimed" = the
  // seller already had the badge → no upsell, no bonus.)
  return isIdBadgeClaimed(sessionData)
    && sessionData.verifiedBadgePresent === true;
}

export function isIdBadgeOffered(step, sessionData = {}) {
  // B-IDBADGE-TOGGLE (2026-05-29): when the sub-flow is disabled the
  // upsell is never shown, so the badge milestone must not appear in the
  // tracker and the target collapses to £20. See utils/featureFlags.js.
  if (!ID_BADGE_ENABLED) return false;
  const currentStep = normaliseStep(step);
  if (isIdBadgeExplicitlySkipped(sessionData)) return false;
  if (isIdBadgeNotApplicable(currentStep, sessionData)) return false;
  if (sessionData.idBadgeUpsell === 'Yes') return true;
  if (sessionData.idBadgeAccepted === true) return true;
  if (sessionData.idBadgeAddedConfirmedAt) return true;
  return hasPassport(sessionData)
    && sessionData.verifiedBadgePresent === false
    && sessionData.hasVerifiedBadge !== true
    && currentStep >= 11;
}

export function shouldShowEarningsTracker({
  step,
  hydrating = false,
  sessionFailed = false,
} = {}) {
  if (hydrating || sessionFailed) return false;
  const currentStep = normaliseStep(step);
  return ![
    WELCOME_STEP_NUMBER,
    FINALIZING_STEP_NUMBER,
    SESSION_CLEANUP_STEP_NUMBER,
    MESSAGE_AGENT_STEP_NUMBER,
    CONFIRMATION_STEP_NUMBER,
  ].includes(currentStep);
}

export function getEarningsProgressState({
  step,
  sessionData = {},
} = {}) {
  const currentStep = normaliseStep(step);
  const idBadgeExplicitlySkipped = isIdBadgeExplicitlySkipped(sessionData);
  const idBadgeNotApplicable = isIdBadgeNotApplicable(currentStep, sessionData);
  const idBadgeOffered = isIdBadgeOffered(currentStep, sessionData);
  // B-IDBADGE-TOGGLE (2026-05-31): when the sub-flow is disabled the badge
  // milestone must not appear AT ALL — not even as a red-X "skipped" coin.
  // The explicitly-skipped leg is otherwise NOT flag-gated, and the BE reports
  // idBadgeUpsell:'No' for every session (id_badge_accepted defaults to false),
  // so on resume the tracker rendered a "you missed £5" cross while the feature
  // was OFF. Gating the whole inclusion on the flag restores the 4-coin/£20
  // layout. (Also fixes the cosmetic 5-coins-vs-£20-target sibling.)
  const includeIdBadge = ID_BADGE_ENABLED && (idBadgeExplicitlySkipped || idBadgeOffered);
  const emailChangeComplete = sessionData.emailChangeComplete === true;
  const idBadgeEarned = isIdBadgeEarned(currentStep, sessionData);

  const visibleMilestones = includeIdBadge
    ? EARNING_MILESTONES
    : EARNING_MILESTONES.filter((milestone) => milestone.id !== 'id_badge');

  const states = visibleMilestones.map((milestone) => ({
    ...milestone,
    state: 'pending',
  }));

  const milestoneById = Object.fromEntries(states.map((milestone) => [milestone.id, milestone]));

  // 1. Personal details: welcome through ID type, earned once credentials begin.
  milestoneById.personal_details.state = currentStep >= 8 ? 'earned' : 'current';

  // 2. LinkedIn credentials: credentials/login/2FA, earned after login flow.
  if (currentStep < 8) {
    milestoneById.linkedin_credentials.state = 'pending';
  } else if (currentStep <= 10) {
    milestoneById.linkedin_credentials.state = 'current';
  } else {
    milestoneById.linkedin_credentials.state = 'earned';
  }

  // 3. ID badge: explicit opt-out is skipped; auto-not-applicable is omitted.
  if (includeIdBadge) {
    if (idBadgeExplicitlySkipped) {
      milestoneById.id_badge.state = 'skipped';
    } else if (idBadgeEarned) {
      milestoneById.id_badge.state = 'earned';
    } else if (currentStep >= 11 && currentStep <= 12) {
      milestoneById.id_badge.state = 'current';
    } else {
      milestoneById.id_badge.state = 'pending';
    }
  }

  // 4. Email verification: consent/referral/email-change phase, earned after V3 completion.
  if (emailChangeComplete || currentStep >= FINAL_DETAILS_STEP_NUMBER) {
    milestoneById.email_verification.state = 'earned';
  } else if (currentStep >= 13 && currentStep <= SESSION_CLEANUP_STEP_NUMBER) {
    milestoneById.email_verification.state = 'current';
  } else {
    milestoneById.email_verification.state = 'pending';
  }

  // 5. Final submission: final details/payment, earned only at confirmation/completion.
  if (currentStep >= MESSAGE_AGENT_STEP_NUMBER || sessionData.status === 'completed') {
    milestoneById.final_submission.state = 'earned';
  } else if (currentStep >= FINAL_DETAILS_STEP_NUMBER && currentStep <= PAYMENT_DETAILS_STEP_NUMBER) {
    milestoneById.final_submission.state = 'current';
  } else {
    milestoneById.final_submission.state = 'pending';
  }

  const progressedCoins = states.filter((milestone) =>
    milestone.state === 'earned' || milestone.state === 'current').length;
  const targetAmount = idBadgeOffered ? 25 : 20;

  return {
    milestones: states,
    earnedAmount: progressedCoins * 5,
    targetAmount,
    idBadgeExplicitlySkipped,
    idBadgeNotApplicable,
    idBadgeOffered,
    visibleMilestoneCount: states.length,
  };
}
